import { Api } from "../../index";
import {
  API_GET_ADD_DRIVER_FORM_FIELDS_JSON,
  API_GET_ADD_VEHICLE_FORM_SCHEMA,
} from "../../Apis/ApiEndPoints";
import { IDynamicFormFieldsJson } from "../../models/user/DynamicForm/DynamicForm";
import { IGetAddDriverFormFieldsResponse } from "../../models/user/Driver/Driver";
import { IGetAddVehicleFormFieldsResponse } from "../../models/user/Vehicle/Vehicle";
import { GetAddDriverFormJson } from "../../store/user/driver/driverActions";
import { GetAddVehicleFormFields } from "../../store/user/vehicle/vehicleActions";
import { AppDispatch } from "../../store/store";
import { VoiceFormIntentId, VoiceFormPolicyContext } from "./voiceFormRegistry";

const dynamicFormSchemaCache = new Map<
  VoiceFormIntentId,
  IDynamicFormFieldsJson
>();

function unwrapFormResponse(
  body: { status?: string; data?: IDynamicFormFieldsJson } | null | undefined
): IDynamicFormFieldsJson | null {
  if (!body || body.status !== "SUCCESS" || !body.data) return null;
  return body.data;
}

export function formatVoiceFormLoadError(err: unknown): string {
  if (typeof err === "string" && err.trim()) return err;
  if (err && typeof err === "object") {
    const e = err as {
      data?: { message?: string; status?: string };
      message?: string;
      status?: string;
    };
    if (e.data?.message) return e.data.message;
    if (e.message) return e.message;
  }
  return "Failed to load form from API.";
}

export async function fetchVoiceDynamicFormSchema(
  formId: VoiceFormIntentId,
  dispatch: AppDispatch,
  prefetched?: {
    driver?: IDynamicFormFieldsJson | null;
    vehicle?: IDynamicFormFieldsJson | null;
  }
): Promise<IDynamicFormFieldsJson | null> {
  const cached = dynamicFormSchemaCache.get(formId);
  if (cached) return cached;

  if (formId === "add_driver" && prefetched?.driver) {
    dynamicFormSchemaCache.set(formId, prefetched.driver);
    return prefetched.driver;
  }
  if (formId === "add_vehicle" && prefetched?.vehicle) {
    dynamicFormSchemaCache.set(formId, prefetched.vehicle);
    return prefetched.vehicle;
  }

  if (formId === "add_driver") {
    const result = await dispatch(GetAddDriverFormJson()).unwrap();
    const schema = unwrapFormResponse(result);
    if (!schema) {
      throw new Error(result.message || "Add driver form not found.");
    }
    dynamicFormSchemaCache.set(formId, schema);
    return schema;
  }

  if (formId === "add_vehicle") {
    const result = await dispatch(GetAddVehicleFormFields()).unwrap();
    const schema = unwrapFormResponse(result) ?? result.data ?? null;
    if (!schema) {
      throw new Error(result.message || "Add vehicle form not found.");
    }
    dynamicFormSchemaCache.set(formId, schema);
    return schema;
  }

  return null;
}

export async function loadAddVehicleFormSchema(): Promise<IDynamicFormFieldsJson | null> {
  try {
    const response = await Api.get<IGetAddVehicleFormFieldsResponse>(
      API_GET_ADD_VEHICLE_FORM_SCHEMA
    );
    return unwrapFormResponse(response.data);
  } catch (err) {
    throw new Error(formatVoiceFormLoadError(err));
  }
}

export async function loadAddDriverFormSchema(): Promise<IDynamicFormFieldsJson | null> {
  try {
    const response = await Api.get<IGetAddDriverFormFieldsResponse>(
      API_GET_ADD_DRIVER_FORM_FIELDS_JSON
    );
    return unwrapFormResponse(response.data);
  } catch (err) {
    throw new Error(formatVoiceFormLoadError(err));
  }
}

/** Inject policy number into common schema field names when a policy is selected. */
export function applyPolicyContextToFormSchema(
  schema: IDynamicFormFieldsJson,
  policy?: VoiceFormPolicyContext
): IDynamicFormFieldsJson {
  if (!policy?.carrierPolicyNo) return schema;
  const policyFields = new Set([
    "carrierPolicyNo",
    "policyNumber",
    "policyNo",
    "carrierPolicyNumber",
  ]);
  return {
    ...schema,
    sections: schema.sections?.map((section) => ({
      ...section,
      fields: section.fields.map((field) =>
        policyFields.has(field.name)
          ? { ...field, value: policy.carrierPolicyNo }
          : field
      ),
    })),
  };
}
